import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GalleryItem from '../components/GalleryItem';
import { useAPI } from '../hooks/useAPI';
import APP_CONFIG from '../config/appConfig';

const Gallery = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEditMode = searchParams.get('edit') === 'true';
  const [selectedItem, setSelectedItem] = useState(null);
  const [videoRef, setVideoRef] = useState(null);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const { data: galleryItems, loading, error, deleteItem } = useAPI('gallery');

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery item?')) {
      try {
        await deleteItem(id);
      } catch (error) {
        alert('Failed to delete gallery item. Please try again.');
      }
    }
  };

  const handleEdit = (item) => {
    const itemId = item._id || item.id;
    navigate(`/admin/gallery?edit=${itemId}`);
  };
  
  const safeGalleryItems = Array.isArray(galleryItems) ? galleryItems : [];
  
  const allItems = safeGalleryItems;
  
  const getItemId = (item) => {
    return item._id || item.id;
  };
  
  const findItemIndex = (itemId) => {
    return allItems.findIndex(item => getItemId(item) === itemId);
  };
  
  const getCurrentIndex = () => {
    if (!selectedItem) return -1;
    return findItemIndex(getItemId(selectedItem));
  };
  
  const goToNext = () => {
    const currentIndex = getCurrentIndex();
    if (currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % allItems.length;
    setSelectedItem(allItems[nextIndex]);
  };
  
  const goToPrevious = () => {
    const currentIndex = getCurrentIndex();
    if (currentIndex === -1) return;
    const prevIndex = currentIndex === 0 ? allItems.length - 1 : currentIndex - 1;
    setSelectedItem(allItems[prevIndex]);
  };

  const handleVideoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (videoRef) {
      if (videoRef.paused) {
        videoRef.play();
        setIsManuallyPaused(false);
      } else {
        videoRef.pause();
        setIsManuallyPaused(true);
      }
    }
  };

  const handleVideoDoubleClick = () => {
    if (videoRef) {
      setIsVideoMuted(!isVideoMuted);
      videoRef.muted = !isVideoMuted;
    }
  };

  useEffect(() => {
    setIsManuallyPaused(false);
  }, [selectedItem]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!selectedItem) return;
      
      if (e.key === 'Escape') {
        setSelectedItem(null);
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    if (selectedItem) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [selectedItem, allItems]);

  if (loading && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-7xl mb-6 script-font text-white" style={{ fontSize: 'clamp(3rem, 8vw, 5rem)' }}>
            {isEditMode ? `Edit ${t('gallery.title')}` : t('gallery.title')}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {isEditMode 
              ? `Manage your gallery items (${allItems.length} total)`
              : t('gallery.subtitle')
            }
          </p>
          {isEditMode && (
            <div className="mt-4">
              <a
                href="/admin/gallery"
                className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-white"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Item
              </a>
            </div>
          )}
        </div>

          {allItems.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {allItems.map((item) => (
              <GalleryItem
                key={getItemId(item)}
                item={item}
                onClick={setSelectedItem}
                isEditMode={isEditMode}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
          ) : (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">No Gallery Items Yet</h3>
              <p className="text-gray-400 mb-6">Start building your gallery by adding your first item.</p>
              {isEditMode && (
                <a
                  href="/admin/gallery"
                  className="inline-flex items-center px-6 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-white"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Your First Item
                </a>
              )}
            </div>
          )}

        {selectedItem && (
          <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-6 right-6 z-10 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              onClick={goToPrevious}
              className="absolute left-6 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-3 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              onClick={goToNext}
              className="absolute right-6 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-3 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <div className="relative max-w-4xl max-h-[90vh] w-full mx-4">
              <div className="bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-400" style={{ boxShadow: '0 0 15px rgba(156, 163, 175, 0.5), 0 0 30px rgba(156, 163, 175, 0.3)' }}>
                {selectedItem.mediaType === 'video' ? (
                  <video
                    ref={setVideoRef}
                    src={selectedItem.mediaUrl ? (selectedItem.mediaUrl.startsWith('http') ? selectedItem.mediaUrl : `${APP_CONFIG.getBaseServerUrl()}${selectedItem.mediaUrl}`) : ''}
                    poster={selectedItem.thumbnail ? (selectedItem.thumbnail.startsWith('http') ? selectedItem.thumbnail : `${APP_CONFIG.getBaseServerUrl()}${selectedItem.thumbnail}`) : ''}
                    preload="auto"
                    autoPlay={!isManuallyPaused}
                    loop
                    playsInline
                    muted={isVideoMuted}
                    className="w-full h-auto max-h-[70vh] object-contain cursor-pointer"
                    controls
                    onClick={handleVideoClick}
                    onDoubleClick={handleVideoDoubleClick}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : (
                  <img
                    src={selectedItem.mediaUrl ? (selectedItem.mediaUrl.startsWith('http') ? selectedItem.mediaUrl : `${APP_CONFIG.getBaseServerUrl()}${selectedItem.mediaUrl}`) : ''}
                    alt={selectedItem.title}
                    loading="eager"
                    className="w-full h-auto max-h-[70vh] object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                )}
                
                {/* Fallback for missing media */}
                <div className="w-full h-64 bg-gray-900 flex items-center justify-center" style={{ display: 'none' }}>
                  <div className="text-center text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                    <p className="text-sm font-medium">Media not available</p>
                  </div>
                </div>
                
                {selectedItem.mediaType === 'video' && (
                  <div className="absolute top-4 right-4 z-10">
                    <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm flex items-center space-x-2">
                      {isVideoMuted ? (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                          </svg>
                          <span>Muted</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                          </svg>
                          <span>Unmuted</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

{/* Title/Category box commented out
              <div className="mt-4 bg-gray-800 rounded-lg p-4 border-2 border-gray-400">
                <h3 className="text-xl font-semibold text-white mb-2">{selectedItem.title}</h3>
                {selectedItem.category && (
                  <span className="inline-block bg-gray-700 text-gray-200 px-3 py-1 rounded-full text-xs font-medium mb-3">
                    {selectedItem.category}
                  </span>
                )}
              </div>
*/}

              <div className="absolute top-6 left-6 z-10">
                <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                  {getCurrentIndex() + 1} / {allItems.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
